import { parseLeaderboardCode, previewMetric, previewRowText } from "./leaderboardPreview";

const BACKGROUNDS = ["yellow", "blue", "green", "purple"];
const PODIUM_PLACES = [2, 1, 3];
const LIST_PLACES = [4, 5, 6, 7, 8, 9, 10];

const SCOPE_LABEL = {
  organization: "本校",
  steminpe: "STEM in PE 聯校",
};

const SUBTITLE_LABEL = {
  member: "每位學生",
  school_cls: "每班",
  grp: "每社",
  organization: "每校",
};

const COUNT_LABEL = {
  member: (count) => `目前共 ${count} 位學生有紀錄`,
  school_cls: (count) => `目前共 ${count} 個班別有紀錄`,
  grp: (count) => `目前共 ${count} 個社有紀錄`,
  organization: (count) => `目前共 ${count} 所學校有紀錄`,
};

const SUBJECT_LABEL = {
  member: "你的名字",
  school_cls: "你的班別",
  grp: "你的社",
  organization: "你的學校",
};

export function slideIntervalMs() {
  const value = Number(process.env.REACT_APP_LEADERBOARD_SLIDE_INTERVAL);
  if (!Number.isFinite(value) || value <= 0) {
    return 30000;
  }
  return value;
}

export function formatSlideCountdown(remainingMs) {
  const totalSeconds = Math.ceil(Number(remainingMs) / 1000);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return "0s";
  }
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

export function slideCountdownRatio(remainingMs, intervalMs) {
  const interval = Number(intervalMs);
  const remaining = Number(remainingMs);
  if (!Number.isFinite(interval) || interval <= 0 || !Number.isFinite(remaining)) {
    return 0;
  }
  return Math.min(1, Math.max(0, remaining / interval));
}

export function slideBackground(value) {
  return BACKGROUNDS.includes(value) ? value : "";
}

export function slideBackgroundUrl(value) {
  return `/assets/images/leaderboard/leaderboard_bg_${slideBackground(value) || "yellow"}.png`;
}

export function podiumAssetUrl(kind, place) {
  const rank = place === 2 || place === 3 ? place : 1;
  return `/assets/images/leaderboard/${kind}_rank${rank}.png`;
}

export function formatSlideClock(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

export const LEADERBOARD_SHOW_WINDOW = "_win_leaderboard_show";

export function leaderboardDisplayPath(code, background) {
  const params = new URLSearchParams();
  if (code) {
    params.set("code", code);
  }
  if (background) {
    params.set("background", background);
  }
  const query = params.toString();
  return query ? `/user/leaderboardDisplay?${query}` : "/user/leaderboardDisplay";
}

export function openLeaderboardDisplay(code, background) {
  const path = leaderboardDisplayPath(code, background);
  const width = window.screen?.availWidth || 1280;
  const height = window.screen?.availHeight || 800;
  const win = window.open(
    path,
    LEADERBOARD_SHOW_WINDOW,
    `popup=yes,width=${width},height=${height},left=0,top=0`,
  );
  if (win) {
    win.focus();
  }
  return win;
}

export function isSlideshowSetting(row) {
  return Number(row?.display_slideshow) === 1
    && Boolean(parseLeaderboardCode(row?.leaderboard_code));
}

function compareNumber(left, right) {
  const delta = Number(left) - Number(right);
  return Number.isFinite(delta) ? delta : 0;
}

export function slideshowSettings(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter(isSlideshowSetting)
    .sort((left, right) => {
      const byPos = compareNumber(left?.display_pos, right?.display_pos);
      if (byPos !== 0) {
        return byPos;
      }
      return compareNumber(left?.id, right?.id);
    });
}

export function previewSetting(rows, code, background) {
  if (!parseLeaderboardCode(code)) {
    return null;
  }
  const match = (Array.isArray(rows) ? rows : []).find((row) => row?.leaderboard_code === code);
  const chosen = slideBackground(background) || slideBackground(match?.background) || "yellow";
  console.log("previewSetting: match", match);
  return {
    id: match?.id ?? 0,
    leaderboard_code: code,
    background: chosen,
    title: match?.title || "",
  };
}

function scopeLabel(parsed) {
  if (!parsed) {
    return "";
  }
  if (parsed.scope === "form" || parsed.scope === "grade") {
    return parsed.scopeArg && parsed.scopeArg !== "_" ? `${parsed.scopeArg} 級` : "";
  }
  if (parsed.scope === "grp") {
    return parsed.scopeArg && parsed.scopeArg !== "_" ? parsed.scopeArg : "";
  }
  return SCOPE_LABEL[parsed.scope] || "";
}

function activityLabel(parsed, skillName) {
  if (!parsed || parsed.item !== "score") {
    return "活動";
  }
  const name = String(skillName || "").trim();
  if (name) {
    return name;
  }
  if (parsed.skillCode && parsed.skillCode !== "all") {
    return parsed.skillCode;
  }
  return "活動";
}

export function slideHeading(code, skillName, title) {
  const parsed = parseLeaderboardCode(code);

  const who = SUBTITLE_LABEL[parsed.groupBy] || "";
  const subtitle = [who, "本學年"].filter(Boolean).join(" • ");
  console.log("slideHeading: title", title);  
  if (title && title !== "") {
    return { title: title, subtitle: subtitle };
  }

  // if (title) {le  
  if (!parsed) {
    return { title: title || "", subtitle: "" };
  }

  let metric = "";
  if (parsed.item === "duration") {
    metric = "全部活動累計時長";
  } else if (parsed.item === "score") {
    const name = activityLabel(parsed, skillName);
    metric = name && name !== "活動" ? `${name}最佳成績` : "最佳成績";
  }
  
  return {
    title: [scopeLabel(parsed), metric].filter(Boolean).join(" • "),
    subtitle: subtitle,
  };
}

function countFooter(code, count) {
  const parsed = parseLeaderboardCode(code);
  const format = COUNT_LABEL[parsed?.groupBy];
  return format ? format(count) : `目前共 ${count} 項有紀錄`;
}

function emptyPanelCopy(code, count, skillName) {
  const parsed = parseLeaderboardCode(code);
  const activity = activityLabel(parsed, skillName);
  if (count <= 0) {
    const subject = SUBJECT_LABEL[parsed?.groupBy] || "你的名字";
    return {
      badge: "尚未有紀錄",
      highlight: "",
      headlineBefore: "誰會成為第一位？",
      headlineAfter: "",
      detail: `完成${activity}，${subject}就會出現在這裡。`,
    };
  }

  return {
    badge: "",
    highlight: String(3 - count),
    headlineBefore: "還有 ",
    headlineAfter: " 個頒獎台位置",
    detail: `完成${activity}，就有機會登上排行榜。`,
  };
}

function presentRow(code, row, orgId, unit) {
  const text = previewRowText(code, row, orgId);
  const metric = previewMetric(code, row, unit);
  const rank = Number(row?.rank);
  return {
    rank: Number.isFinite(rank) && rank > 0 ? rank : null,
    name: text.name,
    meta: text.meta,
    metricValue: metric.value,
    metricUnit: metric.unit,
    champion: rank === 1,
  };
}

export function buildSlideView({ code, rows, skillName, orgId, unit, title }) {
  const top = Array.isArray(rows) ? rows.slice(0, 10) : [];
  const count = top.length;
  
  const heading = slideHeading(code, skillName, title);
  // if (heading) {

  const podium = PODIUM_PLACES.map((place) => {
    const row = top[place - 1] || null;
    const presented = row ? presentRow(code, row, orgId, unit) : null;
    return {
      place,
      filled: Boolean(row),
      showTrophy: Boolean(row) && count >= 3,
      showWaiting: !row && count > 0,
      champion: Boolean(presented?.champion),
      name: presented?.name || "",
      meta: presented?.meta || "",
      metricValue: presented?.metricValue || "",
      metricUnit: presented?.metricUnit || "",
    };
  });

  const list = count >= 3
    ? LIST_PLACES.map((place) => {
      const row = top[place - 1] || null;
      if (!row) {
        return {
          place,
          filled: false,
          rank: place,
          name: "",
          meta: "",
          metricValue: "",
          metricUnit: "",
        };
      }
      const presented = presentRow(code, row, orgId, unit);
      return {
        place,
        filled: true,
        rank: presented.rank || place,
        name: presented.name,
        meta: presented.meta,
        metricValue: presented.metricValue,
        metricUnit: presented.metricUnit,
      };
    })
    : null;

  return {
    title: heading.title,
    subtitle: heading.subtitle,
    footer: count <= 2 ? countFooter(code, count) : "同分同名次 • 顯示前 10 名",
    podium,
    list,
    emptyPanel: count <= 2 ? emptyPanelCopy(code, count, skillName) : null,
  };
}
